import type { Lang } from '../i18n'

export interface Mantra {
  id: string
  text: Record<Lang, string>
  /** Which state this one is for — the app suggests, the user overrides. */
  mood: 'panic' | 'restless' | 'grief' | 'withdrawal' | 'plain'
}

/**
 * Rules these are written to:
 *
 * - No promises. "You will sleep" is a lie and the body knows it, which makes
 *   every other sentence in the app less believable.
 * - No gratitude, no affirmation of worth. At 3am that reads as mockery.
 * - Present tense, short enough to fit inside one exhale.
 * - True even on the worst night. A mantra that is only true on good nights is
 *   useless on the nights you actually reach for it.
 */
export const MANTRAS: Mantra[] = [
  {
    id: 'passes',
    mood: 'panic',
    text: { en: 'This hour passes. It always has.', fa: 'این ساعت می‌گذرد. همیشه گذشته.' },
  },
  {
    id: 'notdanger',
    mood: 'panic',
    text: { en: 'My body is loud. It is not in danger.', fa: 'بدنم بلند است. در خطر نیست.' },
  },
  {
    id: 'rest',
    mood: 'restless',
    text: { en: 'Resting is enough for now.', fa: 'همین استراحت، فعلاً کافی است.' },
  },
  {
    id: 'nothingtodo',
    mood: 'restless',
    text: {
      en: 'There is nothing to solve tonight.',
      fa: 'امشب چیزی برای حل کردن نیست.',
    },
  },
  {
    id: 'heavy',
    mood: 'restless',
    text: { en: 'Heavy. Warm. Still.', fa: 'سنگین. گرم. بی‌حرکت.' },
  },
  {
    id: 'healing',
    mood: 'withdrawal',
    text: {
      en: 'This is my brain repairing, not breaking.',
      fa: 'این ترمیم مغزم است، نه خراب شدنش.',
    },
  },
  {
    id: 'notforever',
    mood: 'withdrawal',
    text: { en: 'Temporary. Awful, and temporary.', fa: 'موقتی است. وحشتناک، و موقتی.' },
  },
  {
    id: 'nofault',
    mood: 'withdrawal',
    text: {
      en: 'I did not do this to myself.',
      fa: 'این را من به سر خودم نیاورده‌ام.',
    },
  },
  {
    id: 'onenight',
    mood: 'grief',
    text: { en: 'One night. Not my whole life.', fa: 'یک شب. نه تمام زندگی‌ام.' },
  },
  {
    id: 'alonebutnot',
    mood: 'grief',
    text: {
      en: 'Someone else is awake right now too.',
      fa: 'همین حالا یکی دیگر هم بیدار است.',
    },
  },
  {
    id: 'breath',
    mood: 'plain',
    text: { en: 'In. Out. That is all.', fa: 'دم. بازدم. همین.' },
  },
  {
    id: 'here',
    mood: 'plain',
    text: { en: 'I am here. It is night. That is all that is true.', fa: 'اینجایم. شب است. همین درست است.' },
  },
  {
    id: 'letgo',
    mood: 'plain',
    text: { en: 'Nothing needs me until morning.', fa: 'تا صبح هیچ‌چیز به من احتیاج ندارد.' },
  },
  {
    id: 'ground',
    mood: 'panic',
    text: { en: 'Feet, bed, floor, ground.', fa: 'پا، تخت، کف، زمین.' },
  },
]

export function mantraById(id: string): Mantra | undefined {
  return MANTRAS.find((m) => m.id === id)
}

export function mantraText(m: Mantra, lang: Lang): string {
  return m.text[lang]
}
